import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import AlertToast from '../../../components/common/AlertToast';
import PolicyTab from '../../../components/common/PolicyTab';
import ClassContextPicker from '../../../components/common/ClassContextPicker';
import ModuleHeaderStrip from '../../../components/common/ModuleHeaderStrip';

const TIER_COLORS = {
  platinum: { bg: '#e0f2fe', text: '#0369a1', solid: '#0ea5e9' },
  gold:     { bg: '#fef3c7', text: '#b45309', solid: '#f59e0b' },
  silver:   { bg: '#f1f5f9', text: '#475569', solid: '#94a3b8' },
  bronze:   { bg: '#fed7aa', text: '#9a3412', solid: '#ea580c' },
};

// ─── Charts (inline SVG so no external deps needed) ─────────────────────────
function BarChart({ data = [], color = '#6366f1', label = '' }) {
  if (!data.length) return <Empty label={label}/>;
  const max = Math.max(...data.map(d => d.value), 100);
  const W = 480, H = 180, padX = 40, padY = 28;
  const plotW = W - padX * 2, plotH = H - padY * 2;
  const barW = Math.max((plotW / data.length) - 6, 10);
  return (
    <div style={{ width: '100%', paddingBottom: `${(H / W) * 100}%`, position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        {[0, 25, 50, 75, 100].map(p => (
          <g key={p}>
            <line x1={padX} x2={W - 12} y1={padY + plotH - (p/100)*plotH} y2={padY + plotH - (p/100)*plotH} stroke="#e2e8f0" strokeWidth="1"/>
            <text x={padX - 6} y={padY + plotH - (p/100)*plotH + 4} fontSize="9" fill="#94a3b8" textAnchor="end">{p}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const h = Math.max((d.value / max) * plotH, 2);
          const x = padX + i * (plotW / data.length) + 3;
          const y = padY + plotH - h;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={h} rx="3" fill={d.color || color}/>
              <text x={x + barW / 2} y={y - 4} fontSize="9" fontWeight="700" fill={d.color || color} textAnchor="middle">{d.value}</text>
              <text x={x + barW / 2} y={H - padY + 14} fontSize="9" fill="#64748b" textAnchor="middle">{(d.label || '').slice(0, 12)}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function LineChart({ data = [], color = '#6366f1', label = '' }) {
  if (!data.length) return <Empty label={label}/>;
  const max = Math.max(...data.map(d => d.value), 100);
  const W = 480, H = 180, padX = 40, padY = 28;
  const plotW = W - padX * 2, plotH = H - padY * 2;
  const xStep = data.length > 1 ? plotW / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const x = padX + (data.length === 1 ? plotW / 2 : i * xStep);
    const y = padY + plotH - (d.value / max) * plotH;
    return { x, y, ...d };
  });
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  return (
    <div style={{ width: '100%', paddingBottom: `${(H / W) * 100}%`, position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        {[0, 25, 50, 75, 100].map(p => (
          <g key={p}>
            <line x1={padX} x2={W - 12} y1={padY + plotH - (p/100)*plotH} y2={padY + plotH - (p/100)*plotH} stroke="#e2e8f0" strokeWidth="1"/>
            <text x={padX - 6} y={padY + plotH - (p/100)*plotH + 4} fontSize="9" fill="#94a3b8" textAnchor="end">{p}</text>
          </g>
        ))}
        <path d={path} fill="none" stroke={color} strokeWidth="2.5"/>
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4" fill={color}/>
            <text x={p.x} y={p.y - 8} fontSize="9" fontWeight="700" fill={color} textAnchor="middle">{p.value}</text>
            <text x={p.x} y={H - padY + 14} fontSize="9" fill="#64748b" textAnchor="middle">{(p.label || '').slice(0, 14)}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function DonutChart({ slices = [], centerLabel = '' }) {
  const total = slices.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <Empty label="distribution"/>;
  const R = 50, r = 30, cx = 80, cy = 80;
  let angle = -Math.PI / 2;
  const paths = slices.map(slice => {
    const sweep = (slice.value / total) * 2 * Math.PI;
    const x1 = cx + R * Math.cos(angle), y1 = cy + R * Math.sin(angle);
    const x2 = cx + R * Math.cos(angle + sweep), y2 = cy + R * Math.sin(angle + sweep);
    const ix1 = cx + r * Math.cos(angle), iy1 = cy + r * Math.sin(angle);
    const ix2 = cx + r * Math.cos(angle + sweep), iy2 = cy + r * Math.sin(angle + sweep);
    const large = sweep > Math.PI ? 1 : 0;
    const d = `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${r} ${r} 0 ${large} 0 ${ix1} ${iy1} Z`;
    angle += sweep;
    return { ...slice, d, pct: Math.round((slice.value / total) * 100) };
  });
  return (
    <div className="d-flex align-items-center gap-3 flex-wrap">
      <svg viewBox="0 0 160 160" style={{ width: 140 }}>
        {paths.map((p, i) => <path key={i} d={p.d} fill={p.color}/>)}
        <circle cx={cx} cy={cy} r={r - 2} fill="white"/>
        {centerLabel && (
          <text x={cx} y={cy + 4} fontSize="11" fontWeight="800" fill="#1e293b" textAnchor="middle">{centerLabel}</text>
        )}
      </svg>
      <div className="d-flex flex-column gap-1 flex-grow-1">
        {paths.map((p, i) => (
          <div key={i} className="d-flex align-items-center gap-2 small">
            <span style={{ width: 12, height: 12, background: p.color, borderRadius: 2 }}/>
            <span className="flex-grow-1">{p.label}</span>
            <span className="fw-bold" style={{ color: p.color }}>{p.pct}%</span>
            <span className="text-muted">({p.value})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HBar({ label, value, max = 100, color = '#6366f1', suffix = '' }) {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between small mb-1">
        <span style={{ color: '#334155' }}>{label}</span>
        <span className="fw-bold" style={{ color }}>{value}{suffix}</span>
      </div>
      <div className="progress" style={{ height: 8 }}>
        <div className="progress-bar" style={{ width: `${pct}%`, background: color }}/>
      </div>
    </div>
  );
}

function Empty({ label = 'data' }) {
  return (
    <div className="text-center text-muted py-3 small">
      <i className="bi bi-bar-chart" style={{ fontSize: 28, opacity: 0.2 }}/>
      <div>No {label} yet</div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────
const blankRecord = {
  studentRecordId: '', studentName: '', admissionNumber: '', classGrade: '', section: '',
  academicYear: '', term: '',
  academics: { overall: 0, subjects: [] },
  sports: { overall: 0, activities: [] },
  cocurricular: { overall: 0, activities: [] },
  attendance: { percentage: 0, daysPresent: 0, daysTotal: 0 },
  behavior: { overall: 75, notes: '' },
  remarks: '',
};

export default function StudentPerformancePage() {
  const { user } = useAuth();
  const role = user?.roleKey || '';
  const canEdit  = ['admin','manager','team_lead','faculty','teacher'].includes(role) || user?.userType === 'super_admin';
  const canAdmin = ['admin','manager'].includes(role) || user?.userType === 'super_admin';

  const [tab, setTab]               = useState('analytics'); // analytics | students | trend | policy
  const [filter, setFilter]         = useState({ classGrade: '', section: '' });
  const [academicYear, setYear]     = useState('');
  const [term, setTerm]             = useState('');

  const [analytics, setAnalytics]   = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [records, setRecords]       = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const [editing, setEditing]       = useState(null);    // current edit record
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(blankRecord);
  const [saving, setSaving]         = useState(false);

  const [trendStudent, setTrendStudent] = useState(null);
  const [trendData, setTrendData]   = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const [toast, setToast]           = useState(null);

  // Build query params from filter+year+term
  const params = (() => {
    const p = {};
    if (filter.classGrade) p.classGrade   = filter.classGrade;
    if (filter.section)    p.section      = filter.section;
    if (academicYear)      p.academicYear = academicYear;
    if (term)              p.term         = term;
    return p;
  })();

  // ─── Load data ────────────────────────────────────────────────────────
  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const r = await api.get('/education/student-performance/analytics', { params });
      setAnalytics(r.data?.data || r.data);
    } catch (e) { setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to load analytics.' }); }
    setAnalyticsLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.classGrade, filter.section, academicYear, term]);

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const r = await api.get('/education/student-performance', { params });
      setRecords(r.data?.records || []);
    } catch (e) { setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to load records.' }); }
    setRecordsLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.classGrade, filter.section, academicYear, term]);

  useEffect(() => { if (tab === 'analytics') loadAnalytics(); }, [tab, loadAnalytics]);
  useEffect(() => { if (tab === 'students')  loadRecords();   }, [tab, loadRecords]);

  // ─── Trend ───────────────────────────────────────────────────────────
  const loadTrend = useCallback(async (studentRecordId) => {
    if (!studentRecordId) return;
    setTrendLoading(true);
    try {
      const r = await api.get(`/education/student-performance/trend/${studentRecordId}`);
      setTrendData(r.data?.data?.records || []);
    } catch (_) { setTrendData([]); }
    setTrendLoading(false);
  }, []);

  // ─── Form helpers ────────────────────────────────────────────────────
  const openCreate = () => { setEditing(null); setForm({ ...blankRecord, classGrade: filter.classGrade, section: filter.section, academicYear, term }); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      studentRecordId: r.studentRecordId || '', studentName: r.studentName, admissionNumber: r.admissionNumber || '',
      classGrade: r.classGrade, section: r.section || '',
      academicYear: r.academicYear || '', term: r.term || '',
      academics:    { overall: r.academics?.overall || 0,    subjects:   r.academics?.subjects   || [] },
      sports:       { overall: r.sports?.overall || 0,       activities: r.sports?.activities    || [] },
      cocurricular: { overall: r.cocurricular?.overall || 0, activities: r.cocurricular?.activities || [] },
      attendance:   { ...r.attendance },
      behavior:     { ...r.behavior },
      remarks: r.remarks || '',
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const save = async () => {
    if (!form.studentName.trim()) return setToast({ type: 'danger', msg: 'Student name is required.' });
    if (!form.classGrade)         return setToast({ type: 'danger', msg: 'Class is required.' });
    setSaving(true);
    try {
      if (editing) await api.put(`/education/student-performance/${editing._id}`, form);
      else         await api.post('/education/student-performance', form);
      setToast({ type: 'success', msg: 'Saved.' });
      closeForm();
      if (tab === 'students')  loadRecords();
      if (tab === 'analytics') loadAnalytics();
    } catch (e) { setToast({ type: 'danger', msg: e.response?.data?.message || 'Save failed.' }); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this performance record?')) return;
    try {
      await api.delete(`/education/student-performance/${id}`);
      setToast({ type: 'success', msg: 'Deleted.' });
      loadRecords();
    } catch (e) { setToast({ type: 'danger', msg: e.response?.data?.message || 'Delete failed.' }); }
  };

  return (
    <div>
      <PageHeader title="Student Performance" icon="bi-graph-up-arrow"
        subtitle="Multi-dimensional tracking — academics, sports, co-curricular, attendance, behavior" />
      {toast && <AlertToast type={toast.type} message={toast.msg} show={true} onClose={() => setToast(null)} />}

      {tab !== 'policy' && analytics && (
        <ModuleHeaderStrip
          icon="bi-graph-up-arrow"
          label="Performance"
          chips={[
            { label: 'Students',    value: analytics.total || 0 },
            { label: 'Overall avg', value: `${analytics.average?.overall || 0}` },
            { label: 'Academics avg', value: `${analytics.average?.academics || 0}` },
            { label: 'Attendance avg', value: `${analytics.average?.attendance || 0}%` },
          ]}
          variant="success"
        />
      )}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${tab === 'analytics' ? 'active' : ''}`} onClick={() => setTab('analytics')}>
          <i className="bi bi-bar-chart-line me-1"/>Analytics & Graphs
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'students' ? 'active' : ''}`} onClick={() => setTab('students')}>
          <i className="bi bi-people me-1"/>Students List
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'trend' ? 'active' : ''}`} onClick={() => setTab('trend')}>
          <i className="bi bi-graph-up me-1"/>Per-Student Trend
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'policy' ? 'active' : ''}`} onClick={() => setTab('policy')}>
          <i className="bi bi-journal-text me-1"/>Policy & Manual
        </button></li>
      </ul>

      {/* Filters (analytics + students) */}
      {(tab === 'analytics' || tab === 'students') && (
        <div className="card mb-3">
          <div className="card-body">
            <ClassContextPicker value={filter} onChange={setFilter} compact fields={['classGrade','section']} />
            <div className="row g-2 mt-2">
              <div className="col-md-3 col-sm-6">
                <label className="form-label small mb-1">Academic Year</label>
                <input className="form-control form-control-sm" placeholder="2026-27" value={academicYear} onChange={e => setYear(e.target.value)} />
              </div>
              <div className="col-md-3 col-sm-6">
                <label className="form-label small mb-1">Term</label>
                <input className="form-control form-control-sm" placeholder="Term 1" value={term} onChange={e => setTerm(e.target.value)} />
              </div>
              {canEdit && tab === 'students' && (
                <div className="col-md-6 text-end d-flex align-items-end justify-content-end">
                  <button className="btn btn-primary btn-sm" onClick={openCreate}>
                    <i className="bi bi-plus-lg me-1"/>Add / Update Performance
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────── ANALYTICS TAB ── */}
      {tab === 'analytics' && (
        analyticsLoading ? (
          <div className="text-center py-4"><LoadingSpinner /></div>
        ) : !analytics || analytics.total === 0 ? (
          <div className="card"><div className="card-body text-center py-5 text-muted">
            <i className="bi bi-bar-chart fs-1 d-block mb-2"/>
            No performance data yet for this filter. {canEdit && <span>Add records on the Students List tab.</span>}
          </div></div>
        ) : (
          <>
            {/* Average score breakdown */}
            <div className="row g-3 mb-3">
              <div className="col-lg-6">
                <div className="card h-100">
                  <div className="card-header py-2 fw-semibold">
                    <i className="bi bi-bar-chart-line me-1 text-primary"/>Average Scores by Dimension
                  </div>
                  <div className="card-body">
                    <HBar label="Academics"    value={analytics.average.academics}    color="#6366f1" />
                    <HBar label="Sports"       value={analytics.average.sports}       color="#22c55e" />
                    <HBar label="Co-curricular" value={analytics.average.cocurricular} color="#a855f7" />
                    <HBar label="Attendance"   value={analytics.average.attendance}   color="#0ea5e9" suffix="%" />
                    <HBar label="Behavior"     value={analytics.average.behavior}     color="#f59e0b" />
                    <hr/>
                    <HBar label="Overall (weighted)" value={analytics.average.overall} color="#ef4444" />
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card h-100">
                  <div className="card-header py-2 fw-semibold">
                    <i className="bi bi-pie-chart me-1 text-success"/>Tier Distribution
                  </div>
                  <div className="card-body">
                    <DonutChart
                      centerLabel={`${analytics.total} students`}
                      slices={['platinum','gold','silver','bronze'].map(t => ({
                        label: t.charAt(0).toUpperCase() + t.slice(1),
                        value: analytics.tierDistribution[t] || 0,
                        color: TIER_COLORS[t].solid,
                      })).filter(s => s.value > 0)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Subject averages bar */}
            {analytics.subjectAverages?.length > 0 && (
              <div className="card mb-3">
                <div className="card-header py-2 fw-semibold">
                  <i className="bi bi-book me-1 text-primary"/>Subject Averages (% across all students)
                </div>
                <div className="card-body">
                  <BarChart data={analytics.subjectAverages.slice(0, 12).map(s => ({ label: s.subject, value: s.average, color: '#6366f1' }))}/>
                </div>
              </div>
            )}

            {/* Class comparison */}
            {analytics.classDistribution?.length > 1 && (
              <div className="card mb-3">
                <div className="card-header py-2 fw-semibold">
                  <i className="bi bi-bar-chart me-1 text-warning"/>Average Total Score by Class & Section
                </div>
                <div className="card-body">
                  <BarChart data={analytics.classDistribution.slice(0, 12).map(c => ({ label: c.label, value: c.average, color: '#f59e0b' }))}/>
                </div>
              </div>
            )}

            {/* Top + bottom performers */}
            <div className="row g-3">
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-header py-2 fw-semibold"><i className="bi bi-trophy me-1 text-success"/>Top Performers</div>
                  <div className="card-body p-0">
                    <PerformerList list={analytics.topPerformers} emptyText="No performers yet." />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-header py-2 fw-semibold"><i className="bi bi-arrow-down-circle me-1 text-danger"/>Needs Attention</div>
                  <div className="card-body p-0">
                    <PerformerList list={analytics.bottomPerformers} emptyText="No data."/>
                  </div>
                </div>
              </div>
            </div>
          </>
        )
      )}

      {/* ───────────────────────────────────────────────── STUDENTS TAB ── */}
      {tab === 'students' && (
        recordsLoading ? (
          <div className="text-center py-4"><LoadingSpinner /></div>
        ) : records.length === 0 ? (
          <div className="card"><div className="card-body text-center py-5 text-muted">
            <i className="bi bi-people fs-1 d-block mb-2"/>
            No performance records yet. {canEdit && <span>Click <strong>Add / Update Performance</strong> to start.</span>}
          </div></div>
        ) : (
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 60 }}>Rank</th>
                      <th>Student</th>
                      <th>Class</th>
                      <th>Acad.</th>
                      <th>Sports</th>
                      <th>Co-curr</th>
                      <th>Attendance</th>
                      <th>Behavior</th>
                      <th>Total</th>
                      <th>Tier</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map(r => {
                      const tc = TIER_COLORS[r.tier] || TIER_COLORS.bronze;
                      return (
                        <tr key={r._id}>
                          <td className="fw-bold text-center">{r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : `#${r.rank}`}</td>
                          <td>
                            <div className="fw-semibold">{r.studentName}</div>
                            {r.admissionNumber && <div className="text-muted small">{r.admissionNumber}</div>}
                          </td>
                          <td>{r.classGrade}{r.section ? ` ${r.section}` : ''}</td>
                          <td>{r.academics?.overall?.toFixed?.(1) || 0}</td>
                          <td>{r.sports?.overall?.toFixed?.(1) || 0}</td>
                          <td>{r.cocurricular?.overall?.toFixed?.(1) || 0}</td>
                          <td>{r.attendance?.percentage?.toFixed?.(1) || 0}%</td>
                          <td>{r.behavior?.overall?.toFixed?.(1) || 0}</td>
                          <td className="fw-bold">{r.totalScore?.toFixed?.(1) || 0}</td>
                          <td>
                            <span className="badge" style={{ background: tc.bg, color: tc.text, fontWeight: 700 }}>
                              {r.tier}
                            </span>
                          </td>
                          <td>
                            {canEdit && (
                              <div className="d-flex gap-1">
                                <button className="btn btn-outline-primary btn-sm py-0 px-2" onClick={() => openEdit(r)} title="Edit">
                                  <i className="bi bi-pencil"/>
                                </button>
                                {r.studentRecordId && (
                                  <button className="btn btn-outline-info btn-sm py-0 px-2"
                                    onClick={() => { setTrendStudent(r); setTab('trend'); loadTrend(r.studentRecordId); }}
                                    title="View trend"><i className="bi bi-graph-up"/></button>
                                )}
                                {canAdmin && (
                                  <button className="btn btn-outline-danger btn-sm py-0 px-2" onClick={() => remove(r._id)} title="Delete">
                                    <i className="bi bi-trash"/>
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      )}

      {/* ───────────────────────────────────────────────── TREND TAB ────── */}
      {tab === 'trend' && (
        <TrendView trendStudent={trendStudent} trendData={trendData} loading={trendLoading} />
      )}

      {tab === 'policy' && <PolicyTab moduleKey="student_performance" label="Student Performance — Manual & Policy"/>}

      {/* ───────────────────────────────────────────────── ADD/EDIT MODAL ── */}
      {showForm && (
        <PerformanceFormModal form={form} setForm={setForm} editing={editing} saving={saving} onClose={closeForm} onSave={save} />
      )}
    </div>
  );
}

function PerformerList({ list = [], emptyText = 'No data.' }) {
  if (!list.length) return <div className="text-center text-muted py-3 small">{emptyText}</div>;
  return (
    <ul className="list-group list-group-flush">
      {list.map((p, i) => {
        const tc = TIER_COLORS[p.tier] || TIER_COLORS.bronze;
        return (
          <li key={p._id} className="list-group-item d-flex justify-content-between align-items-center small">
            <div>
              <span className="fw-bold me-2">#{i + 1}</span>
              {p.studentName}
              <span className="text-muted ms-2">{p.classGrade}{p.section ? ` ${p.section}` : ''}</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold">{(p.totalScore || 0).toFixed(1)}</span>
              <span className="badge" style={{ background: tc.bg, color: tc.text }}>{p.tier}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function TrendView({ trendStudent, trendData, loading }) {
  if (!trendStudent) {
    return (
      <div className="card"><div className="card-body text-center py-5 text-muted">
        <i className="bi bi-graph-up fs-1 d-block mb-2"/>
        Pick a student from the <strong>Students List</strong> tab and click the trend icon to see term-by-term progress here.
      </div></div>
    );
  }
  if (loading) return <div className="text-center py-4"><LoadingSpinner /></div>;

  const labels = trendData.map(r => r.term || r.academicYear || '—');
  return (
    <>
      <div className="card mb-3" style={{ background: 'linear-gradient(135deg,#dcfce7,#dbeafe)', border: 'none' }}>
        <div className="card-body py-3">
          <h5 className="mb-1 fw-bold">{trendStudent.studentName}</h5>
          <div className="small text-muted">
            {trendStudent.classGrade} {trendStudent.section} · {trendData.length} term{trendData.length === 1 ? '' : 's'} on record
          </div>
        </div>
      </div>

      {trendData.length === 0 ? (
        <div className="card"><div className="card-body text-center py-5 text-muted">No history for this student yet.</div></div>
      ) : (
        <div className="row g-3">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header py-2 fw-semibold"><i className="bi bi-graph-up me-1 text-primary"/>Total Score Trend</div>
              <div className="card-body">
                <LineChart color="#6366f1" data={trendData.map((r, i) => ({ label: labels[i], value: r.totalScore || 0 }))}/>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header py-2 fw-semibold"><i className="bi bi-book me-1 text-success"/>Academics Trend</div>
              <div className="card-body">
                <LineChart color="#22c55e" data={trendData.map((r, i) => ({ label: labels[i], value: r.academics?.overall || 0 }))}/>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header py-2 fw-semibold"><i className="bi bi-trophy me-1 text-warning"/>Sports Trend</div>
              <div className="card-body">
                <LineChart color="#f59e0b" data={trendData.map((r, i) => ({ label: labels[i], value: r.sports?.overall || 0 }))}/>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header py-2 fw-semibold"><i className="bi bi-calendar-check me-1 text-info"/>Attendance Trend</div>
              <div className="card-body">
                <LineChart color="#0ea5e9" data={trendData.map((r, i) => ({ label: labels[i], value: r.attendance?.percentage || 0 }))}/>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Form Modal — captures all 5 dimensions ─────────────────────────────────
function PerformanceFormModal({ form, setForm, editing, saving, onClose, onSave }) {
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setNested = (group, k, v) => setForm(p => ({ ...p, [group]: { ...p[group], [k]: v } }));
  const addSubject = () => setForm(p => ({ ...p, academics: { ...p.academics, subjects: [...(p.academics.subjects || []), { subject: '', marks: 0, totalMarks: 100 }] } }));
  const removeSubject = (i) => setForm(p => ({ ...p, academics: { ...p.academics, subjects: p.academics.subjects.filter((_, idx) => idx !== i) } }));
  const updSubject = (i, k, v) => setForm(p => ({ ...p, academics: { ...p.academics, subjects: p.academics.subjects.map((s, idx) => idx === i ? { ...s, [k]: v } : s) } }));
  const addActivity = (group) => setForm(p => ({ ...p, [group]: { ...p[group], activities: [...(p[group].activities || []), { name: '', level: 'school', position: '', score: 0 }] } }));
  const updActivity = (group, i, k, v) => setForm(p => ({ ...p, [group]: { ...p[group], activities: p[group].activities.map((a, idx) => idx === i ? { ...a, [k]: v } : a) } }));
  const removeActivity = (group, i) => setForm(p => ({ ...p, [group]: { ...p[group], activities: p[group].activities.filter((_, idx) => idx !== i) } }));

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{editing ? 'Edit' : 'Add'} Performance Record</h5>
            <button className="btn-close" onClick={onClose}/>
          </div>
          <div className="modal-body">
            <h6 className="text-uppercase text-muted small mb-2">Identity</h6>
            <div className="row g-2 mb-3">
              <div className="col-md-6"><label className="form-label small">Student Name <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" value={form.studentName} onChange={e => setF('studentName', e.target.value)} /></div>
              <div className="col-md-3"><label className="form-label small">Admission No.</label>
                <input className="form-control form-control-sm" value={form.admissionNumber} onChange={e => setF('admissionNumber', e.target.value)} /></div>
              <div className="col-md-3"><label className="form-label small">Term</label>
                <input className="form-control form-control-sm" placeholder="Term 1" value={form.term} onChange={e => setF('term', e.target.value)} /></div>
              <div className="col-12">
                <ClassContextPicker value={{ classGrade: form.classGrade, section: form.section }}
                  onChange={(v) => setForm(p => ({ ...p, classGrade: v.classGrade, section: v.section }))}
                  fields={['classGrade','section']} required={['classGrade']} />
              </div>
              <div className="col-md-3"><label className="form-label small">Academic Year</label>
                <input className="form-control form-control-sm" placeholder="2026-27" value={form.academicYear} onChange={e => setF('academicYear', e.target.value)} /></div>
            </div>

            <h6 className="text-uppercase text-muted small mb-2"><i className="bi bi-book me-1 text-primary"/>Academics</h6>
            <div className="card mb-3">
              <div className="card-body py-2">
                {(form.academics.subjects || []).map((s, i) => (
                  <div key={i} className="row g-2 mb-2 align-items-end">
                    <div className="col-md-4"><label className="form-label small mb-0">Subject</label>
                      <input className="form-control form-control-sm" value={s.subject} onChange={e => updSubject(i, 'subject', e.target.value)} /></div>
                    <div className="col-md-2"><label className="form-label small mb-0">Marks</label>
                      <input type="number" className="form-control form-control-sm" value={s.marks} onChange={e => updSubject(i, 'marks', parseFloat(e.target.value) || 0)} /></div>
                    <div className="col-md-2"><label className="form-label small mb-0">Out of</label>
                      <input type="number" className="form-control form-control-sm" value={s.totalMarks} onChange={e => updSubject(i, 'totalMarks', parseFloat(e.target.value) || 0)} /></div>
                    <div className="col-md-2"><label className="form-label small mb-0">Grade</label>
                      <input className="form-control form-control-sm" placeholder="A+" value={s.grade || ''} onChange={e => updSubject(i, 'grade', e.target.value)} /></div>
                    <div className="col-md-2 text-end">
                      <button className="btn btn-outline-danger btn-sm" onClick={() => removeSubject(i)}><i className="bi bi-x"/></button>
                    </div>
                  </div>
                ))}
                <button className="btn btn-outline-primary btn-sm" onClick={addSubject}><i className="bi bi-plus me-1"/>Add Subject</button>
                <small className="text-muted ms-2">Academics overall is auto-computed as the average percentage.</small>
              </div>
            </div>

            <ScoreSection title="Sports" icon="bi-trophy" iconColor="text-warning" group="sports"
              form={form} setNested={setNested} addActivity={addActivity} updActivity={updActivity} removeActivity={removeActivity} />
            <ScoreSection title="Co-curricular" icon="bi-stars" iconColor="text-info" group="cocurricular"
              form={form} setNested={setNested} addActivity={addActivity} updActivity={updActivity} removeActivity={removeActivity} />

            <h6 className="text-uppercase text-muted small mb-2"><i className="bi bi-calendar-check me-1 text-info"/>Attendance</h6>
            <div className="row g-2 mb-3">
              <div className="col-md-3"><label className="form-label small">Percentage</label>
                <input type="number" min="0" max="100" className="form-control form-control-sm" value={form.attendance.percentage} onChange={e => setNested('attendance', 'percentage', parseFloat(e.target.value) || 0)} /></div>
              <div className="col-md-3"><label className="form-label small">Days Present</label>
                <input type="number" className="form-control form-control-sm" value={form.attendance.daysPresent} onChange={e => setNested('attendance', 'daysPresent', parseInt(e.target.value) || 0)} /></div>
              <div className="col-md-3"><label className="form-label small">Total Days</label>
                <input type="number" className="form-control form-control-sm" value={form.attendance.daysTotal} onChange={e => setNested('attendance', 'daysTotal', parseInt(e.target.value) || 0)} /></div>
            </div>

            <h6 className="text-uppercase text-muted small mb-2"><i className="bi bi-emoji-smile me-1 text-success"/>Behavior</h6>
            <div className="row g-2 mb-3">
              <div className="col-md-3"><label className="form-label small">Score (0–100)</label>
                <input type="number" min="0" max="100" className="form-control form-control-sm" value={form.behavior.overall} onChange={e => setNested('behavior', 'overall', parseFloat(e.target.value) || 0)} /></div>
              <div className="col-md-9"><label className="form-label small">Notes</label>
                <input className="form-control form-control-sm" value={form.behavior.notes || ''} onChange={e => setNested('behavior', 'notes', e.target.value)} /></div>
            </div>

            <h6 className="text-uppercase text-muted small mb-2">Remarks</h6>
            <textarea className="form-control" rows={2} value={form.remarks || ''} onChange={e => setF('remarks', e.target.value)} />
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm me-1"/>}{editing ? 'Save Changes' : 'Create Record'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreSection({ title, icon, iconColor, group, form, setNested, addActivity, updActivity, removeActivity }) {
  const data = form[group] || { overall: 0, activities: [] };
  return (
    <>
      <h6 className="text-uppercase text-muted small mb-2"><i className={`bi ${icon} me-1 ${iconColor}`}/>{title}</h6>
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 mb-2">
            <div className="col-md-3">
              <label className="form-label small">Overall (0–100)</label>
              <input type="number" min="0" max="100" className="form-control form-control-sm" value={data.overall} onChange={e => setNested(group, 'overall', parseFloat(e.target.value) || 0)} />
            </div>
          </div>
          {(data.activities || []).map((a, i) => (
            <div key={i} className="row g-2 mb-2 align-items-end">
              <div className="col-md-3"><label className="form-label small mb-0">Activity</label>
                <input className="form-control form-control-sm" value={a.name} onChange={e => updActivity(group, i, 'name', e.target.value)} /></div>
              <div className="col-md-2"><label className="form-label small mb-0">Level</label>
                <select className="form-select form-select-sm" value={a.level} onChange={e => updActivity(group, i, 'level', e.target.value)}>
                  <option value="school">School</option><option value="inter_school">Inter-school</option>
                  <option value="district">District</option><option value="state">State</option>
                  <option value="national">National</option><option value="international">International</option>
                </select></div>
              <div className="col-md-2"><label className="form-label small mb-0">Position</label>
                <input className="form-control form-control-sm" placeholder="1st / Participant" value={a.position} onChange={e => updActivity(group, i, 'position', e.target.value)} /></div>
              <div className="col-md-2"><label className="form-label small mb-0">Score</label>
                <input type="number" className="form-control form-control-sm" value={a.score} onChange={e => updActivity(group, i, 'score', parseFloat(e.target.value) || 0)} /></div>
              <div className="col-md-3"><label className="form-label small mb-0">Remarks</label>
                <div className="d-flex gap-1">
                  <input className="form-control form-control-sm" value={a.remarks || ''} onChange={e => updActivity(group, i, 'remarks', e.target.value)} />
                  <button className="btn btn-outline-danger btn-sm" onClick={() => removeActivity(group, i)}><i className="bi bi-x"/></button>
                </div>
              </div>
            </div>
          ))}
          <button className="btn btn-outline-primary btn-sm" onClick={() => addActivity(group)}><i className="bi bi-plus me-1"/>Add {title} Activity</button>
        </div>
      </div>
    </>
  );
}
